import { Capacitor } from '@capacitor/core';
import {
  AdMob,
  InterstitialAdPluginEvents,
  AdLoadInfo,
  AdMobError,
  AdmobConsentStatus,
} from '@capacitor-community/admob';

export const INTERSTITIAL_AD_ID = 'ca-app-pub-5019512446625306/6069123437';

// Délai minimum entre deux interstitiels (en ms) — 3 minutes
const MIN_INTERVAL_MS = 3 * 60 * 1000;

let lastShownAt = 0;
let isReady = false;
let isLoading = false;
let listenersRegistered = false;

// Passe à false tant que le consentement publicitaire (UMP/RGPD) est requis
// et n'a pas été obtenu. Par défaut à true : la valeur n'a d'effet que si
// resolveConsent() détermine qu'un consentement est nécessaire (utilisateurs
// EEE, Royaume-Uni, Suisse) et ne l'a pas obtenu.
let adsAllowed = true;

/**
 * Récupère l'état de consentement publicitaire via le SDK Google User
 * Messaging Platform (UMP), intégré à @capacitor-community/admob. Le SDK
 * détecte lui-même la zone géographique de l'utilisateur : hors EEE/UK/CH,
 * le consentement est automatiquement marqué "non requis" et rien ne change.
 * Dans ces zones, le formulaire Google est affiché si nécessaire, et tant
 * que l'utilisateur n'a pas fait de choix, aucune annonce n'est diffusée.
 */
async function resolveConsent(): Promise<void> {
  try {
    let info = await AdMob.requestConsentInfo();

    if (info.status === AdmobConsentStatus.REQUIRED && info.isConsentFormAvailable) {
      info = await AdMob.showConsentForm();
    }

    adsAllowed = info.status !== AdmobConsentStatus.REQUIRED;
  } catch {
    // Impossible de vérifier le consentement (hors ligne, erreur SDK, etc.) :
    // par précaution, on ne diffuse pas d'annonce plutôt que de risquer une
    // diffusion non consentie.
    adsAllowed = false;
  }
}

export async function initAdMob(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  try {
    await AdMob.initialize();
    await resolveConsent();
    if (adsAllowed) await preloadInterstitial();
  } catch {
    // AdMob non disponible — silencieux
  }
}

function registerInterstitialListeners(): void {
  if (listenersRegistered) return;
  listenersRegistered = true;

  AdMob.addListener(InterstitialAdPluginEvents.Loaded, (_info: AdLoadInfo) => {
    isReady = true;
    isLoading = false;
  });

  AdMob.addListener(InterstitialAdPluginEvents.FailedToLoad, (_err: AdMobError) => {
    isReady = false;
    isLoading = false;
  });

  AdMob.addListener(InterstitialAdPluginEvents.Dismissed, () => {
    isReady = false;
    // Pré-charger la prochaine pub après fermeture
    setTimeout(() => preloadInterstitial(), 1000);
  });
}

async function preloadInterstitial(): Promise<void> {
  if (isLoading || isReady) return;
  isLoading = true;

  try {
    registerInterstitialListeners();
    await AdMob.prepareInterstitial({ adId: INTERSTITIAL_AD_ID });
  } catch {
    isLoading = false;
  }
}

/**
 * Affiche un interstitiel après une session d'apprentissage.
 * Respecte l'intervalle minimum et l'état de consentement publicitaire
 * (résolu par resolveConsent() via le SDK UMP de Google, cf. initAdMob).
 */
export async function showSessionAd(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  if (!adsAllowed) return;

  const now = Date.now();
  if (now - lastShownAt < MIN_INTERVAL_MS) return;
  if (!isReady) {
    // Tenter un rechargement si pas prêt
    await preloadInterstitial();
    return;
  }

  try {
    lastShownAt = now;
    await AdMob.showInterstitial();
  } catch {
    isReady = false;
    preloadInterstitial();
  }
}
